import { UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, Injector, OnInit, signal, Signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { FormControlType, FormField, FormGroupComponent } from '@iot-platform/iot-platform-ui';
import { FormsHelpers } from '@iot-platform/iot-platform-utils';
import { LabelValue } from '@iot-platform/models/common';
import { Ward, WardBuilding, WardFloor } from '@iot-platform/models/oyan';
import { TranslatePipe } from '@ngx-translate/core';
import { distinctUntilChanged, finalize, map, of, startWith, switchMap } from 'rxjs';
import { WardsService } from '../../services/wards.service';

export interface WardFormDialogData {
  ward: Ward;
  siteId: string;
  wards: Ward[];
}

export enum WardFormControl {
  NAME = 'name',
  PRIMARY_STOCK = 'primaryStock',
  BUILDING = 'building',
  FLOOR = 'floor'
}

export interface WardFormModel {
  [WardFormControl.NAME]: string | null;
  [WardFormControl.PRIMARY_STOCK]: LabelValue | null;
  [WardFormControl.BUILDING]: WardBuilding | null;
  [WardFormControl.FLOOR]: WardFloor | null;
}

// 'duplicateName' is the error key rendered by the shared form fields.
export const WARD_NAME_NOT_UNIQUE_ERROR = 'duplicateName';

export const WARD_NAME_MAX_LENGTH = 50;

export const WARD_TYPES: LabelValue[] = [
  { value: false, label: 'SITES.WARDS.TABLE.WARD_TYPE.DEFAULT' },
  { value: true, label: 'SITES.WARDS.TABLE.WARD_TYPE.PRIMARY' }
];

@Component({
  selector: 'oyan-ui-ward-form-dialog',
  imports: [ReactiveFormsModule, UpperCasePipe, MatButtonModule, MatCardModule, MatIconModule, MatToolbarModule, TranslatePipe, FormGroupComponent],
  templateUrl: './ward-form-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WardFormDialogComponent implements OnInit {
  readonly data: WardFormDialogData = inject(MAT_DIALOG_DATA);
  private readonly dialogRef: MatDialogRef<WardFormDialogComponent, Ward> = inject(MatDialogRef<WardFormDialogComponent, Ward>);
  private readonly wardsService: WardsService = inject(WardsService);
  private readonly injector: Injector = inject(Injector);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  readonly wardTypes: WritableSignal<LabelValue[]> = signal(WARD_TYPES);
  readonly buildings: WritableSignal<WardBuilding[]> = signal([]);
  readonly floors: WritableSignal<WardFloor[]> = signal([]);
  readonly loadingBuildings: WritableSignal<boolean> = signal(true);
  readonly loadingFloors: WritableSignal<boolean> = signal(false);
  fields: WritableSignal<Partial<FormField>[]> = signal([]);

  readonly form = new FormGroup({
    [WardFormControl.NAME]: new FormControl<string | null>(this.data.ward?.name ?? null, [
      Validators.required,
      Validators.maxLength(WARD_NAME_MAX_LENGTH),
      this.uniqueNameValidator()
    ]),
    [WardFormControl.PRIMARY_STOCK]: new FormControl<LabelValue | null>(WARD_TYPES.find(({ value }) => value === !!this.data.ward?.primaryStock) ?? null, [
      Validators.required
    ]),
    [WardFormControl.BUILDING]: new FormControl<WardBuilding | null>(this.data.ward?.building ?? null, [Validators.required]),
    [WardFormControl.FLOOR]: new FormControl<WardFloor | null>(this.data.ward?.floor ?? null, [Validators.required])
  });

  readonly initialFormState: WritableSignal<WardFormModel | null> = signal(null);
  readonly formValueChanges: Signal<WardFormModel> = toSignal(
    this.form.valueChanges.pipe(
      map(() => this.form.getRawValue()),
      startWith(this.form.getRawValue()),
      takeUntilDestroyed(this.destroyRef)
    )
  );
  readonly canSubmit: Signal<boolean> = computed(() => {
    const initialFormState = this.initialFormState();
    const formValueChanges = this.formValueChanges();
    return this.form.valid && JSON.stringify(formValueChanges) !== JSON.stringify(initialFormState);
  });

  private readonly selectedBuilding: Signal<WardBuilding | null> = toSignal(
    this.form.controls.building.valueChanges.pipe(startWith(this.form.controls.building.value), takeUntilDestroyed(this.destroyRef))
  );
  private readonly disableFloor: Signal<boolean> = computed(() => !this.selectedBuilding());
  private currentBuildingId: string | undefined = this.data.ward?.building?.id;

  private readonly buildingsLookup: Signal<WardBuilding[] | undefined> = toSignal(
    this.wardsService.getBuildings(this.data.siteId).pipe(
      finalize(() => this.loadingBuildings.set(false)),
      takeUntilDestroyed(this.destroyRef)
    )
  );
  private readonly floorsLookup: Signal<WardFloor[] | undefined> = toSignal(
    this.form.controls.building.valueChanges.pipe(
      startWith(this.form.controls.building.value),
      distinctUntilChanged((a: WardBuilding | null, b: WardBuilding | null) => a?.id === b?.id),
      switchMap((building: WardBuilding | null) => {
        if (!building) {
          this.loadingFloors.set(false);
          return of([] as WardFloor[]);
        }
        this.loadingFloors.set(true);
        return this.wardsService.getFloors(building.id).pipe(finalize(() => this.loadingFloors.set(false)));
      }),
      takeUntilDestroyed(this.destroyRef)
    )
  );

  constructor() {
    this.initLookupsEffect();
  }

  ngOnInit(): void {
    this.initializeFields();
    this.initializeFormEffect();
  }

  initializeFields(): void {
    this.fields.set([
      {
        fxFlex: signal('100%'),
        type: FormControlType.TEXT,
        name: signal(WardFormControl.NAME),
        label: signal('SITES.WARDS.FORM.NAME'),
        required: signal(true),
        maxLength: signal(WARD_NAME_MAX_LENGTH)
      },
      {
        fxFlex: signal('100%'),
        type: FormControlType.DROP_DOWN_LIST,
        name: signal(WardFormControl.PRIMARY_STOCK),
        label: signal('SITES.WARDS.FORM.PRIMARY_STOCK'),
        required: signal(true),
        items: this.wardTypes,
        trackBy: (item: LabelValue) => item?.value,
        displayBy: (item: LabelValue) => item?.label,
        compareFn: (a: LabelValue, b: LabelValue) => a?.value === b?.value
      },
      {
        fxFlex: signal('100%'),
        type: FormControlType.DROP_DOWN_LIST,
        name: signal(WardFormControl.BUILDING),
        label: signal('SITES.WARDS.FORM.BUILDING'),
        required: signal(true),
        items: this.buildings,
        showSpinner: this.loadingBuildings,
        trackBy: (item: WardBuilding) => item?.id,
        displayBy: (item: WardBuilding) => item?.name,
        compareFn: (a: WardBuilding, b: WardBuilding) => a?.id === b?.id,
        valueChange: () => this.onBuildingChange()
      },
      {
        fxFlex: signal('100%'),
        type: FormControlType.DROP_DOWN_LIST,
        name: signal(WardFormControl.FLOOR),
        label: signal('SITES.WARDS.FORM.FLOOR'),
        required: signal(true),
        disabled: this.disableFloor,
        items: this.floors,
        showSpinner: this.loadingFloors,
        trackBy: (item: WardFloor) => item?.id,
        displayBy: (item: WardFloor) => item?.name,
        compareFn: (a: WardFloor, b: WardFloor) => a?.id === b?.id
      }
    ]);
  }

  initializeFormEffect(): void {
    effect(
      () => {
        this.initialFormState.set(this.form.getRawValue());
      },
      { injector: this.injector }
    );
  }

  save(): void {
    if (FormsHelpers.isFormValid(this.form)) {
      const { name, primaryStock, building, floor } = this.form.getRawValue();
      this.dialogRef.close({ ...this.data.ward, name: name?.trim(), primaryStock: !!primaryStock?.value, building, floor });
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  private onBuildingChange(): void {
    const buildingId: string | undefined = this.form.controls.building.value?.id;
    if (buildingId !== this.currentBuildingId) {
      this.currentBuildingId = buildingId;
      this.form.controls.floor.setValue(null);
    }
  }

  private initLookupsEffect(): void {
    effect(() => {
      const buildings = this.buildingsLookup();
      if (buildings) {
        this.buildings.set(buildings);
      }
    });
    effect(() => {
      const floors = this.floorsLookup();
      if (floors) {
        this.floors.set(floors);
      }
    });
  }

  private uniqueNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: string = (control.value ?? '').toString().trim().toLowerCase();
      if (!value) {
        return null;
      }
      const exists = (this.data.wards ?? []).some((w: Ward) => w.id !== this.data.ward?.id && (w.name ?? '').trim().toLowerCase() === value);
      return exists ? { [WARD_NAME_NOT_UNIQUE_ERROR]: true } : null;
    };
  }
}
